import type { ReactNode } from "react";
import { AndroidLogo, AppleLogo } from "@/components/ui/icons";
import { cn } from "@/lib/utils";

type AppStoreLinksProps = {
  className?: string;
  centerOnMobile?: boolean;
  inlineOnMobile?: boolean;
  variant?: "list" | "dock" | "provider";
};

const iosInstallUrl = process.env.NEXT_PUBLIC_APP_STORE_IOS_URL || "";
const androidInstallUrl = process.env.NEXT_PUBLIC_APP_STORE_ANDROID_URL || "";

function InstallTile({
  href,
  label,
  description,
  icon,
  centerOnMobile = false,
  inlineOnMobile = false,
  classNameOverride,
  hideDescription = false,
}: {
  href: string;
  label: string;
  description: string;
  icon: ReactNode;
  centerOnMobile?: boolean;
  inlineOnMobile?: boolean;
  classNameOverride?: string;
  hideDescription?: boolean;
}) {
  const rowClassName = cn(
    "group flex min-w-0 gap-3 rounded-[1rem] border px-3 py-3 transition duration-200",
    inlineOnMobile
      ? "items-center"
      : centerOnMobile
        ? "items-center justify-center text-center"
        : "items-center",
    href
      ? "border-[color:var(--color-border)] bg-[color:color-mix(in_oklch,var(--color-background)_96%,white_4%)] hover:-translate-y-px hover:bg-black/[0.02]"
      : "border-dashed border-[color:var(--color-border)] bg-[color:var(--color-muted)]/50 opacity-75",
    classNameOverride,
  );
  const textClassName = cn(
    "min-w-0",
    inlineOnMobile
      ? "flex items-center"
      : centerOnMobile
        ? "grid justify-items-center"
        : "grid",
  );
  const showDescription = !inlineOnMobile && !hideDescription;

  const content = (
    <>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[0.95rem] border border-[color:var(--color-border)] bg-[color:var(--color-background)] text-[color:var(--color-foreground)]">
        {icon}
      </span>
      <div className={textClassName}>
        <p className="text-[0.84rem] font-semibold text-[color:var(--color-foreground)]">
          {label}
        </p>
        {showDescription ? (
          <p className="text-[0.74rem] leading-[1.45] text-[color:var(--color-muted-foreground)]">
            {href ? description : "Link not configured yet"}
          </p>
        ) : null}
      </div>
    </>
  );

  if (!href) {
    return (
      <div className={rowClassName} aria-label={`${label} not configured`}>
        {content}
      </div>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(rowClassName, "no-underline")}
    >
      {content}
    </a>
  );
}

function InstallDockTile({
  href,
  label,
  icon,
  toneClassName,
}: {
  href: string;
  label: string;
  icon: ReactNode;
  toneClassName: string;
}) {
  const tileClassName = cn(
    "inline-flex h-11 w-11 items-center justify-center rounded-full border transition duration-200",
    toneClassName,
    href
      ? "hover:-translate-y-px focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[color:var(--color-ring)]"
      : "opacity-70",
  );

  if (!href) {
    return (
      <span className={tileClassName} aria-label={`${label} not configured`} title={`${label} not configured`}>
        {icon}
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className={tileClassName}
    >
      {icon}
    </a>
  );
}

function ProviderInstallTile({
  href,
  label,
  icon,
}: {
  href: string;
  label: string;
  icon: ReactNode;
}) {
  const className = cn(
    "flex min-h-[clamp(4.6rem,9.4vh,5.15rem)] w-full min-w-0 flex-col items-center justify-center gap-[0.38rem] rounded-[6px] border px-[0.5rem] py-[0.65rem] text-center transition-transform duration-200 sm:min-h-[clamp(4.9rem,9.8vh,5.5rem)] sm:gap-[0.45rem] sm:px-[0.55rem] sm:py-[0.72rem]",
    href
      ? "hover:scale-[1.045] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-100/80"
      : "opacity-70",
  );

  const content = (
    <>
      {icon}
      <span
        className={
          "max-w-full text-center text-[0.74rem] font-[650] leading-[1.2] text-[color:color-mix(in_oklch,var(--color-foreground)_84%,transparent)] sm:text-[0.8rem]"
        }
      >
        {label}
      </span>
    </>
  );

  if (!href) {
    return (
      <span
        className={cn(className, "theme-provider-install-tile")}
        aria-label={`${label} not configured`}
      >
        {content}
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(className, "theme-provider-install-tile no-underline")}
    >
      {content}
    </a>
  );
}

export default function AppStoreLinks({
  className = "",
  centerOnMobile = false,
  inlineOnMobile = false,
  variant = "list",
}: AppStoreLinksProps) {
  if (variant === "dock") {
    return (
      <div className={cn("flex items-center justify-center", className)}>
        <div className="flex items-center gap-2 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-background)] px-2 py-2">
          <InstallDockTile
            href={iosInstallUrl}
            label="iOS Build"
            icon={<AppleLogo className="h-5 w-5" aria-hidden="true" />}
            toneClassName="border-[color:var(--color-border)] bg-[color:var(--color-background)] text-[color:var(--color-foreground)]"
          />
          <InstallDockTile
            href={androidInstallUrl}
            label="Android Build"
            icon={<AndroidLogo className="h-5 w-5" aria-hidden="true" />}
            toneClassName="border-[color:var(--color-info-border)] bg-[color:var(--color-info-soft)] text-[color:var(--color-info-foreground)]"
          />
        </div>
      </div>
    );
  }

  if (variant === "provider") {
    return (
      <div
        className={cn(
          "grid w-full grid-cols-2 gap-[0.6rem] sm:gap-[0.8rem]",
          className,
        )}
      >
        <ProviderInstallTile
          href={iosInstallUrl}
          label="iOS Build"
          icon={
            <AppleLogo
              className="h-5 w-5 text-[color:color-mix(in_oklch,var(--color-foreground)_84%,transparent)] sm:h-[1.4rem] sm:w-[1.4rem]"
              aria-hidden="true"
            />
          }
        />
        <ProviderInstallTile
          href={androidInstallUrl}
          label="Android Build"
          icon={
            <AndroidLogo
              className="h-5 w-5 text-[color:color-mix(in_oklch,var(--color-foreground)_84%,transparent)] sm:h-[1.4rem] sm:w-[1.4rem]"
              aria-hidden="true"
            />
          }
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid gap-3",
        inlineOnMobile ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1",
        className,
      )}
    >
      <InstallTile
        href={iosInstallUrl}
        label="iOS Build"
        description="Open TestFlight / App Store"
        icon={<AppleLogo className="h-5 w-5" aria-hidden="true" />}
        centerOnMobile={centerOnMobile}
        inlineOnMobile={inlineOnMobile}
      />
      <InstallTile
        href={androidInstallUrl}
        label="Android Build"
        description="Open Google Play"
        icon={<AndroidLogo className="h-5 w-5 text-[color:var(--color-info-foreground)]" aria-hidden="true" />}
        centerOnMobile={centerOnMobile}
        inlineOnMobile={inlineOnMobile}
      />
    </div>
  );
}
